import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    CircularProgress,
    Typography,
    Autocomplete,
    TextField,
    IconButton,
    Tooltip,
    Alert,
    Chip,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import EnumsService from '../../Services/enums.service.js';

/**
 * Per-case system account/sort-code settings editor.
 * Values fall back to the hardcoded system defaults when reset / when empty at runtime.
 * Field names match the backend CaseSystemAccountSettings columns (PascalCase).
 * Empty fields are allowed and saved as no override (no required value).
 */
const CARD_FIELDS = [
    { name: 'IncomeVatCardId', labelKey: 'SystemAccountIncomeVat', fallback: 'VAT on sales' },
    { name: 'ExpenseEquipmentVatCardId', labelKey: 'SystemAccountExpenseEquipmentVat', fallback: 'VAT on equipment inputs' },
    { name: 'ExpenseVatCardId', labelKey: 'SystemAccountExpenseVat', fallback: 'VAT on expense inputs' },
    { name: 'CreditAccountIncome', labelKey: 'SystemAccountCreditIncome', fallback: 'Income credit account' },
    { name: 'CreditAccountIncomeNoTax', labelKey: 'SystemAccountCreditIncomeNoTax', fallback: 'Exempt income credit account' },
    { name: 'FixedAssetSaleCreditAccount', labelKey: 'SystemAccountFixedAssetSaleCredit', fallback: 'Fixed-asset sale credit account' },
    { name: 'ClientWithholdingTaxCardId', labelKey: 'SystemAccountClientWithholdingTax', fallback: 'Withholding tax from clients' },
    { name: 'SupplierWithholdingTaxCardId', labelKey: 'SystemAccountSupplierWithholdingTax', fallback: 'Withholding tax from suppliers' },
    { name: 'GeneralCheckCardId', labelKey: 'SystemAccountGeneralCheck', fallback: 'Checks vault' },
    { name: 'AcceptanceCheckCardId', labelKey: 'SystemAccountAcceptanceCheck', fallback: 'Receipt checks' },
    { name: 'PaymentCheckCardId', labelKey: 'SystemAccountPaymentCheck', fallback: 'Payment checks' },
    { name: 'GeneralCashCardId', labelKey: 'SystemAccountGeneralCash', fallback: 'Cash vault' },
    { name: 'AcceptanceCashCardId', labelKey: 'SystemAccountAcceptanceCash', fallback: 'Receipt cash' },
    { name: 'PaymentCashCardId', labelKey: 'SystemAccountPaymentCash', fallback: 'Payment cash' },
    { name: 'GeneralCreditCardCardId', labelKey: 'SystemAccountGeneralCreditCard', fallback: 'Credit-card vault' },
    { name: 'AcceptanceCreditCardCardId', labelKey: 'SystemAccountAcceptanceCreditCard', fallback: 'Receipt credit cards' },
    { name: 'PaymentCreditCardCardId', labelKey: 'SystemAccountPaymentCreditCard', fallback: 'Payment credit cards' },
    { name: 'GeneralBankCardId', labelKey: 'SystemAccountGeneralBank', fallback: 'Bank transfers vault' },
    { name: 'AcceptanceBankTransactionCardId', labelKey: 'SystemAccountAcceptanceBankTransaction', fallback: 'Receipt bank transfers' },
    { name: 'PaymentBankTransactionCardId', labelKey: 'SystemAccountPaymentBankTransaction', fallback: 'Payment bank transfers' },
    { name: 'GeneralOffsetCardId', labelKey: 'SystemAccountGeneralOffset', fallback: 'Offsets vault' },
    { name: 'ExemptPurchasesCardId', labelKey: 'SystemAccountExemptPurchases', fallback: 'Exempt purchases card' },
    { name: 'LawyerDepositsCardId', labelKey: 'SystemAccountLawyerDeposits', fallback: 'Lawyer deposits card' },
    { name: 'LawyerExpensesCardId', labelKey: 'SystemAccountLawyerExpenses', fallback: 'Lawyer expenses card' },
    { name: 'LawyerVariousFeesCardId', labelKey: 'SystemAccountLawyerVariousFees', fallback: 'Lawyer various fees card' },
    { name: 'LedgerProfessionalismCardId', labelKey: 'SystemAccountLedgerProfessionalism', fallback: 'Ledger professionalism card' },
    { name: 'LedgerSalesIncomeCardId', labelKey: 'SystemAccountLedgerSalesIncome', fallback: 'Ledger sales income card' },
];

const SORT_FIELDS = [
    { name: 'SortCodeDebit', labelKey: 'SystemSortCodeDebit', fallback: 'Debit sort code' },
    { name: 'SortCodeCredit', labelKey: 'SystemSortCodeCredit', fallback: 'Credit sort code' },
    { name: 'SortCodeDebitCredit', labelKey: 'SystemSortCodeDebitCredit', fallback: 'Debit/credit sort code' },
    { name: 'SortCodeIncomeNoTax', labelKey: 'SystemSortCodeIncomeNoTax', fallback: 'Exempt income sort code' },
    { name: 'SortCodeExemptPurchases', labelKey: 'SystemSortCodeExemptPurchases', fallback: 'Exempt purchases sort code' },
    { name: 'FixedAssetSaleSortCode', labelKey: 'SystemSortCodeFixedAssetSale', fallback: 'Fixed-asset sale sort code' },
    { name: 'SortCodeExportSales', labelKey: 'SystemSortCodeExportSales', fallback: 'Export sales sort code' },
    { name: 'SortCodePalestineSales', labelKey: 'SystemSortCodePalestineSales', fallback: 'Palestine sales sort code' },
    { name: 'SortCodeImportDeclaration', labelKey: 'SystemSortCodeImportDeclaration', fallback: 'Import declaration sort code' },
    { name: 'SortCodePalestinePurchase', labelKey: 'SystemSortCodePalestinePurchase', fallback: 'Palestine purchases sort code' },
    { name: 'SortCodeSelfInvoiceIncome', labelKey: 'SystemSortCodeSelfInvoiceIncome', fallback: 'Self-invoice income sort code' },
    { name: 'SortCodeSelfInvoiceExpense', labelKey: 'SystemSortCodeSelfInvoiceExpense', fallback: 'Self-invoice expense sort code' },
    { name: 'SortCodeSelfInvoiceCM', labelKey: 'SystemSortCodeSelfInvoiceCM', fallback: 'Self-invoice C+M sort code' },
    { name: 'LawyerDepositsSortCode', labelKey: 'SystemSortCodeLawyerDeposits', fallback: 'Lawyer deposits sort code' },
    { name: 'LawyerExpensesSortCode', labelKey: 'SystemSortCodeLawyerExpenses', fallback: 'Lawyer expenses sort code' },
    { name: 'LawyerVariousFeesSortCode', labelKey: 'SystemSortCodeLawyerVariousFees', fallback: 'Lawyer various fees sort code' },
    { name: 'LedgerProfessionalismSortCode', labelKey: 'SystemSortCodeLedgerProfessionalism', fallback: 'Ledger professionalism sort code' },
    { name: 'LedgerSalesIncomeSortCode', labelKey: 'SystemSortCodeLedgerSalesIncome', fallback: 'Sales income sort code' },
];

/** Fields whose remap affects VAT / withholding history — show a soft warning when changed. */
const SENSITIVE_FIELDS = new Set([
    'IncomeVatCardId',
    'ExpenseEquipmentVatCardId',
    'ExpenseVatCardId',
    'ClientWithholdingTaxCardId',
    'SupplierWithholdingTaxCardId',
]);

const toCamel = (s) => s.charAt(0).toLowerCase() + s.slice(1);

const SystemAccountSettingsDialog = ({
    open,
    onClose,
    caseId,
    token,
    accountCardsData,
    sortCodesOptions,
    onSaved,
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [defaults, setDefaults] = useState({});
    const [values, setValues] = useState({});
    const [initialValues, setInitialValues] = useState({});

    useEffect(() => {
        if (!open || !caseId || !token) return;
        let cancelled = false;
        setError(null);
        setLoading(true);

        const load = async () => {
            try {
                const resp = await EnumsService.GetCaseSystemAccountSettings(caseId, token);
                if (!resp.ok) throw new Error(await resp.text());
                const data = await resp.json();
                if (cancelled) return;

                const resolved = data.resolved || {};
                const defs = data.defaults || {};
                const overridden = data.overridden || {};
                const map = {};
                [...CARD_FIELDS, ...SORT_FIELDS].forEach(({ name }) => {
                    // Only prefill when this case has an explicit override; otherwise leave empty.
                    const hasOverride = overridden[name] === true;
                    const raw = hasOverride ? resolved[toCamel(name)] : '';
                    map[name] = raw == null || raw === '' ? '' : String(raw);
                });
                setDefaults(defs);
                setValues(map);
                setInitialValues(map);
            } catch (err) {
                if (!cancelled) setError(err?.message || t('ErrorLoadingData') || 'Error loading data');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [open, caseId, token, t]);

    const cardOptions = useMemo(() => {
        return (accountCardsData || []).map((c) => ({
            value: String(c.cardId),
            label: `${c.cardId} - ${c.name || ''}`,
        }));
    }, [accountCardsData]);

    const cardOptionIds = useMemo(
        () => new Set(cardOptions.map((o) => o.value)),
        [cardOptions]
    );

    const sortOptions = useMemo(() => {
        return (sortCodesOptions || [])
            .filter((s) => s && s.value !== '')
            .map((s) => ({ value: String(s.value), label: s.label || String(s.value) }));
    }, [sortCodesOptions]);

    const setField = useCallback((name, newValue) => {
        setValues((prev) => ({ ...prev, [name]: newValue ?? '' }));
    }, []);

    const resetField = useCallback((name) => {
        // Reset = clear override (empty), so runtime uses the hardcoded default.
        setValues((prev) => ({ ...prev, [name]: '' }));
    }, []);

    const resetAll = useCallback(() => {
        const map = {};
        [...CARD_FIELDS, ...SORT_FIELDS].forEach(({ name }) => {
            map[name] = '';
        });
        setValues(map);
    }, []);

    const changedSensitive = useMemo(() => {
        return [...SENSITIVE_FIELDS].some(
            (name) => (values[name] ?? '') !== (initialValues[name] ?? '')
        );
    }, [values, initialValues]);

    const overriddenCount = useMemo(() => {
        return [...CARD_FIELDS, ...SORT_FIELDS].filter(
            ({ name }) => String(values[name] ?? '').trim() !== ''
        ).length;
    }, [values]);

    const handleSave = async () => {
        setSaving(true);
        try {
            // Empty / missing fields are sent as '' (no value). Card IDs not in this case's
            // account cards are also cleared so save never requires a missing card.
            const payload = {};
            CARD_FIELDS.forEach(({ name }) => {
                const raw = String(values[name] ?? '').trim();
                payload[name] = raw && cardOptionIds.has(raw) ? raw : '';
            });
            SORT_FIELDS.forEach(({ name }) => {
                payload[name] = String(values[name] ?? '').trim();
            });
            const resp = await EnumsService.UpdateCaseSystemAccountSettings(caseId, payload, token);
            if (!resp.ok) {
                const msg = await resp.text();
                throw new Error(msg || 'Save failed');
            }
            toast.success(t('SystemAccountSettingsSaved') || 'System account settings saved');
            if (onSaved) await onSaved();
            onClose();
        } catch (err) {
            toast.error(err?.message || t('SystemAccountSettingsSaveFailed') || 'Failed to save system account settings');
        } finally {
            setSaving(false);
        }
    };

    const renderFieldRow = (field, options, freeSolo) => {
        const { name, labelKey, fallback } = field;
        const current = String(values[name] ?? '');
        const defaultValue = String(defaults[name] ?? '');
        const isOverridden = current.trim() !== '';
        const selectedOption = options.find((o) => o.value === current) || null;
        // Missing / unknown card ids render as empty — no value required.
        const displayValue = selectedOption
            || (freeSolo && current ? { value: current, label: current } : null);

        return (
            <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Box sx={{ flex: '0 0 45%', minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: 13, fontWeight: isOverridden ? 700 : 400 }} noWrap>
                        {t(labelKey) || fallback}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {(t('DefaultValue') || 'Default')}: {defaultValue || '—'}
                    </Typography>
                </Box>
                <Autocomplete
                    size="small"
                    freeSolo={freeSolo}
                    clearOnEscape
                    options={options}
                    value={displayValue}
                    isOptionEqualToValue={(o, v) => o?.value === v?.value}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o?.label ?? '')}
                    onChange={(_, newVal) => {
                        if (typeof newVal === 'string') setField(name, newVal.trim());
                        else setField(name, newVal ? newVal.value : '');
                    }}
                    onInputChange={freeSolo ? (_, newInput, reason) => {
                        if (reason === 'input') setField(name, newInput.trim());
                        if (reason === 'clear') setField(name, '');
                    } : undefined}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={defaultValue || (t('Optional') || 'Optional')}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
                        />
                    )}
                    sx={{ flex: 1, minWidth: 0 }}
                />
                <Tooltip title={t('ResetToDefault') || 'Reset to default'}>
                    <span>
                        <IconButton size="small" onClick={() => resetField(name)} disabled={!isOverridden}>
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        );
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir={dir}>
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" component="span">
                        {t('SystemAccountSettings') || 'System account settings'}
                    </Typography>
                    {overriddenCount > 0 && (
                        <Chip
                            size="small"
                            label={`${overriddenCount} ${t('Overridden') || 'overridden'}`}
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: 11, height: 22 }}
                        />
                    )}
                </Box>
                <Button size="small" startIcon={<RestartAltIcon />} onClick={resetAll} disabled={loading || saving}>
                    {t('ResetAllToDefaults') || 'Reset all to defaults'}
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pb: 0 }}>
                {loading ? (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="textSecondary">{t('Loading') || 'Loading...'}</Typography>
                        {error && <Typography variant="body2" color="error">{error}</Typography>}
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 1, fontSize: 12 }}>
                            {t('SystemAccountSettingsInfo')
                                || 'These cards and sort codes are used automatically when generating documents and calculating VAT, withholding tax and reports for this case. Historical entries are not changed.'}
                        </Alert>
                        {changedSensitive && (
                            <Alert severity="warning" sx={{ mb: 1, fontSize: 12 }}>
                                {t('SystemAccountSettingsSensitiveWarning')
                                    || 'You changed a VAT or withholding-tax card. Existing journal entries keep pointing at the old card; only new documents will use the new one.'}
                            </Alert>
                        )}

                        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>
                            {t('AccountCards') || 'Account cards'}
                        </Typography>
                        {CARD_FIELDS.map((f) => renderFieldRow(f, cardOptions, false))}

                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>
                            {t('SortCodes') || 'Sort codes'}
                        </Typography>
                        {SORT_FIELDS.map((f) => renderFieldRow(f, sortOptions, true))}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose} disabled={saving}>{t('cancelButton') || 'Cancel'}</Button>
                <Button variant="contained" onClick={handleSave} disabled={loading || saving || !!error}>
                    {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {t('Save') || 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SystemAccountSettingsDialog;
